/**
 * Canonical date/time primitives for the engine port.
 *
 * The Python engine works with NAIVE local datetimes (datetime.date / datetime.time /
 * datetime.datetime) plus an explicit UTC-offset-in-hours that it manages itself.
 * JS `Date` conflates the host timezone with the value, which corrupts exactly the
 * minute/second-level astronomy this project exists for — so the engine NEVER uses
 * `Date` for astronomical math. Every ported module uses these plain structs instead.
 *
 * `Date` is permitted in exactly two places:
 *   - "today" capture (todayLocal), mirroring Python's date.today()
 *   - the Intl-based timezone resolution in lib/timezones.ts (browser tzdb lookups)
 */

export interface PlainDate {
  year: number;
  month: number; // 1-12, like Python
  day: number; // 1-31
}

export interface PlainTime {
  hour: number; // 0-23
  minute: number;
  second: number;
}

export interface PlainDateTime extends PlainDate, PlainTime {}

export function plainDate(year: number, month: number, day: number): PlainDate {
  return { year, month, day };
}

export function plainTime(hour: number, minute = 0, second = 0): PlainTime {
  return { hour, minute, second };
}

export function plainDateTime(
  year: number,
  month: number,
  day: number,
  hour = 0,
  minute = 0,
  second = 0,
): PlainDateTime {
  return { year, month, day, hour, minute, second };
}

export function combine(d: PlainDate, t: PlainTime): PlainDateTime {
  return { year: d.year, month: d.month, day: d.day, hour: t.hour, minute: t.minute, second: t.second };
}

export function dateOnly(dt: PlainDateTime): PlainDate {
  return { year: dt.year, month: dt.month, day: dt.day };
}

export function timeOnly(dt: PlainDateTime): PlainTime {
  return { hour: dt.hour, minute: dt.minute, second: dt.second };
}

/** date.today() equivalent — host-local calendar date. */
export function todayLocal(): PlainDate {
  const now = new Date();
  return { year: now.getFullYear(), month: now.getMonth() + 1, day: now.getDate() };
}

/** Python datetime.now().isoformat(timespec="seconds") — host-local "YYYY-MM-DDTHH:MM:SS". */
export function nowISOSeconds(): string {
  const now = new Date();
  const d: PlainDateTime = {
    year: now.getFullYear(),
    month: now.getMonth() + 1,
    day: now.getDate(),
    hour: now.getHours(),
    minute: now.getMinutes(),
    second: now.getSeconds(),
  };
  return `${formatISODate(d)}T${formatISOTime(d)}`;
}

// ---------------------------------------------------------------------------
// ISO parsing / formatting (mirrors Python's date.isoformat / fromisoformat)
// ---------------------------------------------------------------------------

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const TIME_RE = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/;

export function parseISODate(s: string): PlainDate {
  const m = DATE_RE.exec(s.trim());
  if (!m) throw new Error(`Invalid ISO date: ${JSON.stringify(s)}`);
  const d = { year: Number(m[1]), month: Number(m[2]), day: Number(m[3]) };
  if (!isValidDate(d)) throw new Error(`Invalid calendar date: ${s}`);
  return d;
}

export function tryParseISODate(s: unknown): PlainDate | null {
  if (typeof s !== "string") return null;
  try {
    return parseISODate(s);
  } catch {
    return null;
  }
}

/** Accepts HH:MM and HH:MM:SS, like the Python intake. */
export function parseISOTime(s: string): PlainTime {
  const m = TIME_RE.exec(s.trim());
  if (!m) throw new Error(`Invalid time: ${JSON.stringify(s)}`);
  const t = { hour: Number(m[1]), minute: Number(m[2]), second: m[3] ? Number(m[3]) : 0 };
  if (t.hour > 23 || t.minute > 59 || t.second > 59) throw new Error(`Invalid time: ${s}`);
  return t;
}

export function tryParseISOTime(s: unknown): PlainTime | null {
  if (typeof s !== "string") return null;
  try {
    return parseISOTime(s);
  } catch {
    return null;
  }
}

export function parseISODateTime(s: string): PlainDateTime {
  const [datePart, timePart] = s.trim().split(/[T ]/);
  const d = parseISODate(datePart);
  const t = timePart ? parseISOTime(timePart) : { hour: 0, minute: 0, second: 0 };
  return combine(d, t);
}

const pad2 = (n: number) => String(n).padStart(2, "0");
const pad4 = (n: number) => String(n).padStart(4, "0");

export function formatISODate(d: PlainDate): string {
  return `${pad4(d.year)}-${pad2(d.month)}-${pad2(d.day)}`;
}

export function formatISOTime(t: PlainTime): string {
  return `${pad2(t.hour)}:${pad2(t.minute)}:${pad2(t.second)}`;
}

export function formatHM(t: PlainTime): string {
  return `${pad2(t.hour)}:${pad2(t.minute)}`;
}

export function formatISODateTime(dt: PlainDateTime): string {
  return `${formatISODate(dt)} ${formatISOTime(dt)}`;
}

// ---------------------------------------------------------------------------
// Calendar arithmetic (proleptic Gregorian ordinal — matches Python's
// date.toordinal / fromordinal, so day-difference math ports 1:1)
// ---------------------------------------------------------------------------

export function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

export function daysInMonth(year: number, month: number): number {
  if (month === 2 && isLeapYear(year)) return 29;
  return DAYS_IN_MONTH[month - 1];
}

export function isValidDate(d: PlainDate): boolean {
  return (
    Number.isInteger(d.year) &&
    Number.isInteger(d.month) &&
    Number.isInteger(d.day) &&
    d.month >= 1 &&
    d.month <= 12 &&
    d.day >= 1 &&
    d.day <= daysInMonth(d.year, d.month)
  );
}

/** Python date.toordinal(): days since 0001-01-01 = ordinal 1. */
export function toOrdinal(d: PlainDate): number {
  const y = d.year - 1;
  let days = y * 365 + Math.floor(y / 4) - Math.floor(y / 100) + Math.floor(y / 400);
  for (let m = 1; m < d.month; m++) days += daysInMonth(d.year, m);
  return days + d.day;
}

/** Python date.fromordinal(). */
export function fromOrdinal(ordinal: number): PlainDate {
  // Binary search the year, then walk months. Ordinals here are small (~7e5).
  let year = Math.max(1, Math.floor(ordinal / 365.2425));
  while (toOrdinal({ year: year + 1, month: 1, day: 1 }) <= ordinal) year++;
  while (toOrdinal({ year, month: 1, day: 1 }) > ordinal) year--;
  let rem = ordinal - toOrdinal({ year, month: 1, day: 1 }) + 1;
  let month = 1;
  while (rem > daysInMonth(year, month)) {
    rem -= daysInMonth(year, month);
    month++;
  }
  return { year, month, day: rem };
}

/** Python date.weekday(): Monday=0 .. Sunday=6 (0001-01-01, ordinal 1, was a Monday). */
export function weekday(d: PlainDate): number {
  return (toOrdinal(d) + 6) % 7;
}

export function addDays(d: PlainDate, days: number): PlainDate {
  return fromOrdinal(toOrdinal(d) + days);
}

/** (a - b) in whole days, like Python (a - b).days. */
export function diffDays(a: PlainDate, b: PlainDate): number {
  return toOrdinal(a) - toOrdinal(b);
}

export function compareDates(a: PlainDate, b: PlainDate): number {
  return toOrdinal(a) - toOrdinal(b);
}

export function datesEqual(a: PlainDate, b: PlainDate): boolean {
  return a.year === b.year && a.month === b.month && a.day === b.day;
}

export function secondsOfDay(t: PlainTime): number {
  return t.hour * 3600 + t.minute * 60 + t.second;
}

export function timeFromSecondsOfDay(total: number): PlainTime {
  const s = ((Math.round(total) % 86400) + 86400) % 86400;
  return { hour: Math.floor(s / 3600), minute: Math.floor((s % 3600) / 60), second: s % 60 };
}

/** Epoch-style seconds for a naive datetime (no timezone applied). */
export function naiveSeconds(dt: PlainDateTime): number {
  return toOrdinal(dt) * 86400 + secondsOfDay(dt);
}

export function addSeconds(dt: PlainDateTime, seconds: number): PlainDateTime {
  const total = naiveSeconds(dt) + Math.round(seconds);
  const ord = Math.floor(total / 86400);
  const d = fromOrdinal(ord);
  return combine(d, timeFromSecondsOfDay(total - ord * 86400));
}

/** (a - b) in seconds for naive datetimes, like Python timedelta.total_seconds(). */
export function diffSeconds(a: PlainDateTime, b: PlainDateTime): number {
  return naiveSeconds(a) - naiveSeconds(b);
}

/** Fractional years between two dates (Python: (a-b).days / 365.25 convention). */
export function yearsBetween(later: PlainDate, earlier: PlainDate): number {
  return diffDays(later, earlier) / 365.25;
}
